import { SocialLogin } from '@capgo/capacitor-social-login';
import { authClient } from './auth-client';

/**
 * Native social sign-in (Capacitor only). The system Apple/Google sheet
 * issues an ID token which better-auth verifies server-side via
 * signIn.social({ idToken }) — no browser redirect, no cookies. OAuth inside
 * the embedded WebView is not an option (Google rejects it with
 * disallowed_useragent), which is why this native path exists.
 */

let initialized = false;

const ensureInitialized = async (): Promise<void> => {
  if (initialized) return;
  await SocialLogin.initialize({
    google: {
      // Audience of the issued ID token; the API verifies against its
      // GOOGLE_*_CLIENT_ID list, so these must match the same environment.
      webClientId: process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID ?? '',
      iOSClientId: process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID,
    },
    apple: {},
  });
  initialized = true;
};

export const signInWithNativeProvider = async (provider: 'apple' | 'google'): Promise<void> => {
  // Refuse BEFORE touching the native module: calling the Google SDK with an
  // empty client id crashes the app rather than throwing a catchable error.
  if (
    provider === 'google' &&
    (!process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID || !process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID)
  ) {
    throw new Error('Google sign-in is not configured in this build yet — use email sign-in');
  }

  await ensureInitialized();

  // Google: always show the account chooser (forcePrompt on iOS,
  // filterByAuthorizedAccounts off on Android) — without it the SDK silently
  // reuses the last-authorized account, which strands multi-account users in
  // the wrong login. Apple's sheet always lets the user confirm.
  const login = await SocialLogin.login(
    provider === 'google'
      ? {
          provider: 'google',
          options: {
            scopes: ['email', 'profile'],
            forcePrompt: true,
            filterByAuthorizedAccounts: false,
          },
        }
      : { provider: 'apple', options: { scopes: ['email', 'profile'] } }
  );

  const idToken = (login.result as { idToken?: string | null })?.idToken;
  if (!idToken) {
    throw new Error(`No identity token returned from native ${provider} sign-in`);
  }

  const response = await authClient.signIn.social({
    provider,
    idToken: { token: idToken },
  });

  if (response.error) {
    throw new Error(response.error.message ?? `${provider} sign-in failed`);
  }
};
